/**
 * PremiumAccessService.js
 * 
 * Service to manage premium content access based on user subscriptions
 * Users pay 1000 DEC per 30 days for access to premium features
 */

import authService from './AuthService';

// Global toggle to enable/disable premium features for all users
const PREMIUM_FEATURES_ENABLED = false; // Set to false to make all premium features free for everyone

// Subscription pricing
const SUBSCRIPTION_PRICE = 1000; // DEC
const SUBSCRIPTION_DURATION_DAYS = 30;

// Premium feature configuration
const PREMIUM_FEATURES = {
  SPS_DISTRIBUTION_CHART: {
    id: 'sps_distribution_chart',
    name: 'SPS Distribution Chart',
    description: 'View detailed SPS token distribution data',
  },
  SPS_DISTRIBUTION_HISTORY: {
    id: 'sps_distribution_history',
    name: 'SPS Distribution History',
    description: 'Track historical SPS token distribution changes over time',
  },
  SPS_TOP_LIST: {
    id: 'sps_top_list',
    name: 'SPS Top Holders List',
    description: 'View detailed SPS top holders ranking',
  },
  SPS_BURN_CHART: {
    id: 'sps_burn_chart',
    name: 'SPS Burn Bar Chart',
    description: 'View detailed SPS burn statistics',
  },
  DEC_DISTRIBUTION_CHART: {
    id: 'dec_distribution_chart',
    name: 'DEC Distribution Chart',
    description: 'View DEC token distribution overview',
  },
  DEC_COMPANY_HISTORY: {
    id: 'dec_company_history',
    name: 'DEC Company History',
    description: 'View Splinterlands company DEC holdings over time',
  }
};

// Cache für Benutzer-Subscription-Daten
let userSubscriptionCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 Minuten

/**
 * Fetches user subscription data from the backend API
 * @param {string} username - The player username
 * @returns {Promise<Object>} User subscription data
 */
const fetchUserSubscription = async (username) => {
  try {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    const response = await fetch(`${baseUrl}/premium/check/${username}/`);
    
    if (!response.ok) {
      if (response.status === 404) {
        // User not found
        return { has_access: false, reason: 'no_subscription' };
      }
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    // Return no access on error
    return { has_access: false, reason: 'api_error' };
  }
};

/**
 * Get cached or fresh user subscription data
 * @param {string} username - The player username
 * @returns {Promise<Object>} User subscription data
 */
const getUserSubscriptionData = async (username) => {
  const cacheKey = username.toLowerCase();
  const now = Date.now();
  
  // Check if we have cached data that's still valid
  if (userSubscriptionCache[cacheKey] && 
      userSubscriptionCache[cacheKey].timestamp > now - CACHE_TTL) {
    return userSubscriptionCache[cacheKey].data;
  }
  
  // Fetch fresh data
  const subscriptionData = await fetchUserSubscription(username);
  
  // Cache the data
  userSubscriptionCache[cacheKey] = {
    data: subscriptionData,
    timestamp: now
  };
  
  return subscriptionData;
};

/**
 * Checks if a user has access to a specific premium feature
 * @param {string} featureId - The ID of the feature to check
 * @returns {Object} Access status and details
 */
const checkPremiumAccess = async (featureId) => {
  const authData = authService.getCurrentUser();
  const feature = PREMIUM_FEATURES[featureId];

  if (!feature) {
    return {
      hasAccess: false,
      reason: 'feature_not_found',
      message: 'The requested premium feature does not exist.'
    };
  }

  if (!authData || !authData.username) {
    return {
      hasAccess: false,
      reason: 'not_logged_in',
      feature,
      subscriptionPrice: SUBSCRIPTION_PRICE,
      subscriptionDuration: SUBSCRIPTION_DURATION_DAYS,
      message: 'Login required to access premium features.'
    };
  }

  const username = authData.username;

  try {
    // Get user's subscription data from API
    const subscriptionData = await getUserSubscriptionData(username);
    
    if (subscriptionData.has_access) {
      return {
        hasAccess: true,
        feature,
        isWhitelisted: subscriptionData.is_whitelisted || false,
        subscription: subscriptionData.subscription || null,
        message: subscriptionData.message || 'Access granted!'
      };
    } else {
      return {
        hasAccess: false,
        reason: subscriptionData.reason || 'no_subscription',
        feature,
        subscriptionPrice: SUBSCRIPTION_PRICE,
        subscriptionDuration: SUBSCRIPTION_DURATION_DAYS,
        message: subscriptionData.message || `Subscribe for ${SUBSCRIPTION_PRICE} DEC to access premium features for ${SUBSCRIPTION_DURATION_DAYS} days.`
      };
    }
  } catch (error) {
    console.error('Error checking premium access:', error);
    return {
      hasAccess: false,
      reason: 'api_error',
      feature,
      subscriptionPrice: SUBSCRIPTION_PRICE,
      subscriptionDuration: SUBSCRIPTION_DURATION_DAYS,
      message: 'Unable to verify premium access. Please try again later.'
    };
  }
};

/**
 * Get user's full subscription information
 * @param {string} username - Optional username (if not provided, will use current logged in user)
 * @returns {Promise<Object>} Object containing subscription details
 */
const getUserAccessSummary = async (username = null) => {
  const user = username || (authService.getCurrentUser()?.username);
  
  if (!user) {
    return {
      isLoggedIn: false,
      hasAccess: false,
      subscriptionPrice: SUBSCRIPTION_PRICE,
      subscriptionDuration: SUBSCRIPTION_DURATION_DAYS
    };
  }

  try {
    // Get subscription data from API
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    const response = await fetch(`${baseUrl}/premium/subscription/${user}/`);
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      isLoggedIn: true,
      ...data
    };
  } catch (error) {
    console.error('Error getting user access summary:', error);
    return {
      isLoggedIn: true,
      hasAccess: false,
      error: 'Unable to load subscription data',
      subscriptionPrice: SUBSCRIPTION_PRICE,
      subscriptionDuration: SUBSCRIPTION_DURATION_DAYS
    };
  }
};

/**
 * Create a new subscription for the current user
 * @param {string} txHash - Optional transaction hash if payment already made
 * @returns {Promise<Object>} Subscription creation result
 */
const createSubscription = async (txHash = null) => {
  const authData = authService.getCurrentUser();
  
  if (!authData || !authData.username) {
    return {
      success: false,
      error: 'Not logged in'
    };
  }

  try {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    const response = await fetch(`${baseUrl}/premium/subscribe/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: authData.username,
        tx_hash: txHash,
        auto_confirm: false
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Clear cache to force refresh
    const cacheKey = authData.username.toLowerCase();
    delete userSubscriptionCache[cacheKey];
    
    return data;
  } catch (error) {
    console.error('Error creating subscription:', error);
    return {
      success: false,
      error: 'Failed to create subscription'
    };
  }
};

/**
 * Confirm payment for a subscription with blockchain verification
 * @param {number} subscriptionId - ID of the subscription to confirm
 * @param {string} txHash - Transaction hash from Hive blockchain
 * @returns {Promise<Object>} Confirmation result
 */
const confirmPayment = async (subscriptionId, txHash) => {
  try {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    const response = await fetch(`${baseUrl}/premium/subscription/${subscriptionId}/confirm/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Clear cache to force refresh
    const authData = authService.getCurrentUser();
    if (authData && authData.username) {
      const cacheKey = authData.username.toLowerCase();
      delete userSubscriptionCache[cacheKey];
    }
    
    return data;
  } catch (error) {
    console.error('Error confirming payment:', error);
    return {
      success: false,
      error: error.message || 'Failed to confirm payment'
    };
  }
};

/**
 * Get list of all premium features
 * @returns {Promise<Object>} List of features and pricing
 */
const getPremiumFeatures = async () => {
  try {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.elmo-burns.com/api/v1';
    const response = await fetch(`${baseUrl}/premium/features/`);
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting premium features:', error);
    return {
      features: Object.values(PREMIUM_FEATURES),
      subscription_price: SUBSCRIPTION_PRICE,
      subscription_duration_days: SUBSCRIPTION_DURATION_DAYS
    };
  }
};

const PremiumAccessService = {
  PREMIUM_FEATURES,
  PREMIUM_FEATURES_ENABLED,
  SUBSCRIPTION_PRICE,
  SUBSCRIPTION_DURATION_DAYS,
  checkPremiumAccess,
  getUserAccessSummary,
  createSubscription,
  confirmPayment,
  getPremiumFeatures,
  fetchUserSubscription,
  getUserSubscriptionData,
};

export default PremiumAccessService;